package com.fantasytenis.shared.domain.survivor.entity

import com.fantasytenis.shared.domain.bracket.entity.LockOverride
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import kotlinx.serialization.Serializable

/**
 * Survivor contest variation types
 */
enum class SurvivorVariation(val displayName: String, val description: String) {
    STRAIGHT_KNOCKOUT("Straight Knockout", "One loss and you're out"),
    STRIKE_SYSTEM("Strike System", "Get 2-3 strikes before elimination"),
    REBUY_ALLOWED("Rebuy Allowed", "Can pay to re-enter after elimination");

    companion object {
        fun fromApiValue(value: Int): SurvivorVariation {
            return when (value) {
                1 -> STRAIGHT_KNOCKOUT
                2 -> STRIKE_SYSTEM
                3 -> REBUY_ALLOWED
                else -> STRAIGHT_KNOCKOUT
            }
        }
    }
}

/**
 * Survivor contest status
 */
enum class SurvivorContestStatus(val displayName: String) {
    UPCOMING("Upcoming"),
    ACTIVE("Active"),
    COMPLETED("Completed"),
    CANCELLED("Cancelled"),
    PAYING_OUT("Paying Out");

    companion object {
        fun fromApiValue(value: Int): SurvivorContestStatus {
            return when (value) {
                1 -> UPCOMING
                2 -> ACTIVE
                3 -> COMPLETED
                4 -> CANCELLED
                5 -> PAYING_OUT
                else -> UPCOMING
            }
        }
    }
}

/**
 * Survivor entry status
 */
enum class SurvivorEntryStatus(val displayName: String, val isAlive: Boolean) {
    ALIVE("Alive", true),
    ELIMINATED("Eliminated", false),
    REBUY_ELIGIBLE("Rebuy Eligible", false),
    WITHDRAWN("Withdrawn", false),
    WINNER("Winner", true),
    PAID("Paid", true);

    companion object {
        fun fromApiValue(value: Int): SurvivorEntryStatus {
            return when (value) {
                1 -> ALIVE
                2 -> ELIMINATED
                3 -> REBUY_ELIGIBLE
                4 -> WITHDRAWN
                5 -> WINNER
                6 -> PAID
                else -> ALIVE
            }
        }
    }
}

/**
 * Survivor pick result
 */
enum class SurvivorPickResult(val displayName: String, val isWin: Boolean, val isLoss: Boolean) {
    PENDING("Pending", false, false),
    WON("Won", true, false),
    LOST("Lost", false, true),
    WALKOVER_WIN("Walkover Win", true, false),
    WALKOVER_LOSS("Walkover Loss", false, true),
    NO_PICK("No Pick", false, true);

    companion object {
        fun fromApiValue(value: Int): SurvivorPickResult {
            return when (value) {
                1 -> PENDING
                2 -> WON
                3 -> LOST
                4 -> WALKOVER_WIN
                5 -> WALKOVER_LOSS
                6 -> NO_PICK
                else -> PENDING
            }
        }
    }
}

/**
 * Payout tier for survivor contests
 */
@Serializable
data class SurvivorPayoutTierEntity(
    val minRank: Int,
    val maxRank: Int,
    val amount: Int?, // In cents
    val percentage: Double?,
    val description: String?
)

/**
 * Survivor Contest Entity
 */
@Serializable
data class SurvivorContestEntity(
    val id: String,
    val name: String,
    val description: String?,
    val drawId: String,
    val tournamentName: String,
    val variation: SurvivorVariation,
    val entryFee: Int, // In cents
    val prizePool: Int, // In cents
    val maxEntries: Int?,
    val currentEntries: Int,
    val aliveEntries: Int,
    val maxEntriesPerUser: Int,
    val currentRound: RoundName?,
    val roundDeadline: String?, // ISO timestamp
    val status: SurvivorContestStatus,
    val strikeLimit: Int,
    val rebuyDeadlineRound: RoundName?,
    val rebuyFee: Int, // In cents
    val payoutStructure: List<SurvivorPayoutTierEntity>,
    val isFeatured: Boolean,
    val createdAt: String,
    // Lock override fields for admin manual control
    val lockOverride: LockOverride = LockOverride.NONE,
    val lockOverrideBy: String? = null,
    val lockOverrideAt: String? = null,
    val lockOverrideReason: String? = null
) {
    val isFull: Boolean get() = maxEntries != null && currentEntries >= maxEntries
    val spotsRemaining: Int? get() = maxEntries?.let { it - currentEntries }
    val eliminatedCount: Int get() = currentEntries - aliveEntries

    val entryFeeDisplay: String
        get() = if (entryFee == 0) "FREE" else "$${entryFee / 100.0}"

    val prizePoolDisplay: String
        get() = "$${prizePool / 100.0}"

    val rebuyFeeDisplay: String
        get() = if (rebuyFee == 0) "FREE" else "$${rebuyFee / 100.0}"

    val variationDescription: String
        get() = when (variation) {
            SurvivorVariation.STRAIGHT_KNOCKOUT -> "One loss = eliminated"
            SurvivorVariation.STRIKE_SYSTEM -> "$strikeLimit strikes before elimination"
            SurvivorVariation.REBUY_ALLOWED -> "Rebuy available through ${rebuyDeadlineRound?.displayName ?: "R32"}"
        }

    val canEnter: Boolean get() = (status == SurvivorContestStatus.UPCOMING || isManuallyUnlocked) && !isFull
    val isActive: Boolean get() = status == SurvivorContestStatus.ACTIVE
    val isComplete: Boolean get() = status == SurvivorContestStatus.COMPLETED ||
            status == SurvivorContestStatus.PAYING_OUT

    /**
     * Determines if the contest is locked for submissions.
     * Admin override takes precedence over round deadline.
     */
    val isRoundLocked: Boolean
        get() = when (lockOverride) {
            LockOverride.FORCE_UNLOCKED -> false
            LockOverride.FORCE_LOCKED -> true
            LockOverride.NONE -> roundDeadline?.let {
                try {
                    kotlinx.datetime.Clock.System.now() >= kotlinx.datetime.Instant.parse(it)
                } catch (e: Exception) {
                    false
                }
            } ?: false
        }

    /** True if an admin has manually unlocked this contest */
    val isManuallyUnlocked: Boolean get() = lockOverride == LockOverride.FORCE_UNLOCKED

    /** True if an admin has manually locked this contest */
    val isManuallyLocked: Boolean get() = lockOverride == LockOverride.FORCE_LOCKED

    /** True if there is any lock override (not using time-based logic) */
    val hasLockOverride: Boolean get() = lockOverride != LockOverride.NONE
}

/**
 * Survivor Entry Entity
 * User's entry in a survivor contest
 */
@Serializable
data class SurvivorEntryEntity(
    val id: String,
    val contestId: String,
    val userId: String,
    val userName: String?,
    val status: SurvivorEntryStatus,
    val strikesUsed: Int,
    val survivedRounds: Int,
    val eliminatedInRound: RoundName?,
    val eliminatedByMatchId: String?,
    val rank: Int?,
    val payoutAmount: Int, // In cents
    val rebuyCount: Int,
    val lastPickRound: RoundName?,
    val createdAt: String,
    val eliminatedAt: String?,
    // User profile info for leaderboard display
    val username: String? = null,
    val avatarUrl: String? = null
) {
    val isAlive: Boolean get() = status.isAlive
    val canRebuy: Boolean get() = status == SurvivorEntryStatus.REBUY_ELIGIBLE

    val displayName: String get() = username?.let { "@$it" } ?: userName ?: "Player"

    val initials: String get() = when {
        username != null -> username.take(2).uppercase()
        userName != null -> userName.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
        else -> "?"
    }

    val payoutDisplay: String
        get() = if (payoutAmount > 0) "$${payoutAmount / 100.0}" else ""

    val strikesRemaining: Int
        get() = 0 // Will be calculated based on contest strikeLimit

    fun strikesRemaining(strikeLimit: Int): Int = strikeLimit - strikesUsed
}

/**
 * Survivor Pick Entity
 * Individual pick for a round
 */
@Serializable
data class SurvivorPickEntity(
    val id: String,
    val entryId: String,
    val round: RoundName,
    val playerId: String,
    val playerName: String,
    val playerSeed: Int?,
    val playerCountry: String?,
    val opponentId: String?,
    val opponentName: String?,
    val opponentSeed: Int?,
    val matchId: String?,
    val matchTime: String?,
    val result: SurvivorPickResult,
    val matchScore: String?,
    val pickedAt: String,
    val resolvedAt: String?
) {
    val isPending: Boolean get() = result == SurvivorPickResult.PENDING
    val isWin: Boolean get() = result.isWin
    val isLoss: Boolean get() = result.isLoss

    val displaySeed: String?
        get() = playerSeed?.let { "[$it]" }

    val opponentDisplaySeed: String?
        get() = opponentSeed?.let { "[$it]" }

    val matchupDisplay: String
        get() = "$playerName vs ${opponentName ?: "TBD"}"

    val resultDisplay: String
        get() = when (result) {
            SurvivorPickResult.PENDING -> "Pending"
            SurvivorPickResult.WON -> "Won${matchScore?.let { " $it" } ?: ""}"
            SurvivorPickResult.LOST -> "Lost${matchScore?.let { " $it" } ?: ""}"
            SurvivorPickResult.WALKOVER_WIN -> "Walkover Win"
            SurvivorPickResult.WALKOVER_LOSS -> "Walkover Loss"
            SurvivorPickResult.NO_PICK -> "No Pick Made"
        }
}

/**
 * Available Player Entity
 * A player available to pick for the current round
 */
@Serializable
data class AvailablePlayerEntity(
    val playerId: String,
    val playerName: String,
    val seed: Int?,
    val country: String?,
    val atpRanking: Int?,
    val matchId: String,
    val opponentId: String?,
    val opponentName: String?,
    val opponentSeed: Int?,
    val matchTime: String?,
    val court: String?,
    val ownershipPercentage: Double,
    val alreadyUsed: Boolean,
    val isMatchLocked: Boolean
) {
    val isAvailable: Boolean get() = !alreadyUsed && !isMatchLocked

    val displaySeed: String?
        get() = seed?.let { "[$it]" }

    val opponentDisplaySeed: String?
        get() = opponentSeed?.let { "[$it]" }

    val matchupDisplay: String
        get() = "$playerName vs ${opponentName ?: "TBD"}"

    val ownershipDisplay: String
        get() = "${ownershipPercentage.toInt()}%"

    val statusDisplay: String
        get() = when {
            alreadyUsed -> "Already Used"
            isMatchLocked -> "Match Started"
            else -> "Available"
        }
}

/**
 * Round Summary Entity
 * Summary of a round in a survivor contest
 */
@Serializable
data class SurvivorRoundSummaryEntity(
    val round: RoundName,
    val roundStatus: Int, // 1=upcoming, 2=picking, 3=locked, 4=in_progress, 5=completed
    val deadline: String?,
    val totalEntries: Int,
    val aliveAfter: Int,
    val eliminatedCount: Int,
    val matchesCompleted: Int,
    val totalMatches: Int
) {
    val statusDisplay: String
        get() = when (roundStatus) {
            1 -> "Upcoming"
            2 -> "Make Your Pick"
            3 -> "Picks Locked"
            4 -> "In Progress"
            5 -> "Completed"
            else -> "Unknown"
        }

    val isPickingOpen: Boolean get() = roundStatus == 2
    val isComplete: Boolean get() = roundStatus == 5
    val matchProgress: String get() = "$matchesCompleted/$totalMatches matches"
}

/**
 * Survivor Leaderboard Entry Entity
 */
@Serializable
data class SurvivorLeaderboardEntryEntity(
    val entryId: String,
    val userId: String,
    val displayName: String,
    val status: SurvivorEntryStatus,
    val strikesUsed: Int,
    val survivedRounds: Int,
    val lastPickPlayer: String?,
    val lastPickResult: SurvivorPickResult?
)

/**
 * Entry with picks - combines entry data with pick history
 */
@Serializable
data class SurvivorEntryWithPicksEntity(
    val entry: SurvivorEntryEntity,
    val picks: List<SurvivorPickEntity>
) {
    val picksByRound: Map<RoundName, SurvivorPickEntity>
        get() = picks.associateBy { it.round }

    fun getPickForRound(round: RoundName): SurvivorPickEntity? {
        return picks.find { it.round == round }
    }
}
